import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { AlertTriangle, Info, AlertCircle } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Link } from "./link";

const alertVariants = cva(
  "relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5",
  {
    variants: {
      variant: {
        default: "bg-orange-50 border-orange-300 text-orange-800",
        info: "bg-sky-50 text-sky-800 border-sky-200",
        destructive:
          "bg-red-50 text-red-800 *:data-[slot=alert-description]:text-destructive/90",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const Alert = ({
  className,
  variant,
  severity,
  ctaText,
  ctaHref,
  children,
  ...props
}: React.ComponentProps<"div"> &
  VariantProps<typeof alertVariants> & {
    severity?: "warning" | "error" | "info";
    ctaText?: string;
    ctaHref?: string;
  }): JSX.Element => {
  // Map MUI severity to shadcn variant if severity is provided
  const mappedVariant = severity ? mapSeverityToVariant(severity) : variant;

  const getIcon = () => {
    switch (mappedVariant) {
      case "info":
        return <Info className="h-4 w-4 text-sky-600" />;
      case "destructive":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
    }
  };

  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant: mappedVariant }), className)}
      {...props}
    >
      {getIcon()}
      <div className="col-start-2 flex items-start justify-between">
        <div className="flex-1">
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child) && child.type === AlertTitle) {
              return React.cloneElement(child, {
                variant: mappedVariant,
              } as any);
            }
            return child;
          })}
        </div>
        {ctaText && ctaHref && (
          <div className="ml-4 flex-shrink-0">
            <Button size="sm" variant="outline" asChild>
              <Link to={ctaHref} asChild>
                {ctaText}
              </Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Map MUI Alert severity to shadcn Alert variant
const mapSeverityToVariant = (severity: "warning" | "error" | "info") => {
  switch (severity) {
    case "warning":
      return "default";
    case "error":
      return "destructive";
    case "info":
      return "info";
    default:
      return "default";
  }
};

const AlertTitle = ({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> &
  VariantProps<typeof alertVariants>): JSX.Element => {
  const getTitleColor = () => {
    switch (variant) {
      case "info":
        return "text-sky-950";
      case "destructive":
        return "text-red-950";
      default:
        return "text-orange-950";
    }
  };

  return (
    <div
      data-slot="alert-title"
      className={cn(
        "col-start-2 line-clamp-1 min-h-4 font-semibold tracking-tight",
        getTitleColor(),
        className,
      )}
      {...props}
    />
  );
};

const AlertDescription = ({
  className,
  ...props
}: React.ComponentProps<"div">): JSX.Element => {
  return (
    <div
      data-slot="alert-description"
      className={cn("col-start-2 text-sm [&_p]:leading-relaxed", className)}
      {...props}
    />
  );
};

export { Alert, AlertTitle, AlertDescription };
